import { TablePaginationConfig } from 'antd'
import { AddressLink, SearchVestingParams, VestingData } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { Abi, formatEther } from 'viem'

import abiVestingV2 from 'abi/abi-vesting-v2.json'
import { AppAdmin } from 'api/app-admin'
import { ContractStatusEnum } from 'constants/index'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { useVestingContextV2 } from 'context/vesting-context-provider-v2'
import { publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

interface IHookVestingDataV2 {
  includeDataContract?: boolean
}

export const useVestingDataV2 = ({ includeDataContract = false }: IHookVestingDataV2) => {
  const { type, scVesting } = useVestingContextV2()

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<VestingData[]>([])
  const [total, setTotal] = useState(0)

  const defaultSearchParams = {
    ownerAddress: '',
    page: DEFAULT_PAGE_INDEX,
    limit: DEFAULT_PAGE_SIZE,
    status: null,
    type: type,
  }

  const [searchParams, setSearchParams] =
    useState<Partial<SearchVestingParams>>(defaultSearchParams)

  const getDataContract = useCallback(
    async (data: VestingData[]) => {
      try {
        const contracts = data.map((item: VestingData) => ({
          address: scVesting as AddressLink,
          abi: abiVestingV2 as Abi,
          functionName: 'getVestingEntries',
          args: [item.ownerAddress],
        }))

        const results = await publicWallet.multicall({ contracts })
        return results
      } catch (error) {
        logError('useVestingDataV2 getDataContract error:', error)
      }
    },
    [scVesting]
  )

  const getData = useCallback(
    async (params: SearchVestingParams) => {
      setLoading(true)
      try {
        if (!type) return
        const res = await AppAdmin.getListVesting({ ...params, type })
        if (res?.data?.code === 200) {
          const results = res?.data?.data?.results ?? []
          setTotal(res?.data?.data?.totalResults ?? 0)

          if (results.length && includeDataContract && scVesting) {
            const dataContract = await getDataContract(results)
            const resultData = results.map((item: VestingData, index: number) => {
              const entry = dataContract?.[index]
              if (entry?.status === ContractStatusEnum.SUCCESS && entry.result) {
                const [totalAmount, totalClaimed, totalClaimable, totalLocked] = entry.result as [
                  bigint,
                  bigint,
                  bigint,
                  bigint,
                  any[],
                ]
                return {
                  ...item,
                  amountContract: Number(formatEther(totalAmount)),
                  claimed: Number(formatEther(totalClaimed)),
                  unlock: Number(formatEther(totalClaimable)),
                  totalLocked: Number(formatEther(totalLocked)),
                }
              }
              return item
            })
            setData(resultData)
          } else {
            setData(results)
          }
        } else {
          setData([])
          setTotal(0)
        }
      } catch (error) {
        setData([])
        setTotal(0)
        logError('useVestingDataV2 getListVesting error:', error)
      } finally {
        setLoading(false)
      }
    },
    [type, includeDataContract, scVesting, getDataContract]
  )

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: page, pageSize: limit } = pagination
    onChangeSearchParams({ page: page, limit: limit })
  }

  const onChangeSearchParams = (payload?: Partial<SearchVestingParams>) => {
    setSearchParams((pre) => ({ ...pre, ...payload }))
  }

  const onLoadData = () => {
    onChangeSearchParams(defaultSearchParams)
    getData(defaultSearchParams as SearchVestingParams)
  }

  useEffect(() => {
    getData(searchParams as SearchVestingParams)
  }, [getData, searchParams])

  return {
    loading,
    data,
    total,
    searchParams,
    getData,
    handleTableChange,
    onLoadData,
    onChangeSearchParams,
  }
}
